// Adaptience — frozen taxonomy + scalable simulation catalogue.
// Add future simulations by appending records here; they automatically appear in the
// library, the correct category and the Navbar filters. No component changes required.
//
// Schema is intentionally forward-compatible. Commercial/account fields
// (access, pricing, entitlement, completion history, recommendation metadata)
// can be added later WITHOUT rebuilding the library.

export const CATEGORIES = [
  {
    slug: "everyday-life-skills",
    name: "Everyday Life Skills",
    description: "Practical situations requiring everyday independence, judgment and capability.",
    seoTitle: "Everyday Life Skills Simulations | Adaptience",
    seoDescription:
      "Practise essential life skills through interactive simulations of everyday situations involving communication, problem-solving, decisions and practical challenges.",
  },
  {
    slug: "social-relationships",
    name: "Social & Relationships",
    description: "Friendships, communication, belonging, conflict, boundaries and relationships.",
    seoTitle: "Social & Relationship Skills Simulations | Adaptience",
    seoDescription:
      "Practise navigating social situations, relationships, difficult conversations and interpersonal challenges through interactive real-life simulations.",
  },
  {
    slug: "decisions-pressure",
    name: "Decisions & Pressure",
    description: "Choices involving uncertainty, influence, risk or pressure.",
    seoTitle: "Decision-Making & Pressure Simulations | Adaptience",
    seoDescription:
      "Practise decision-making, problem-solving and responding under pressure through interactive simulations of challenging real-world situations.",
  },
  {
    slug: "safety-emergencies",
    name: "Safety & Emergencies",
    description: "Situations involving personal safety, emergencies and unexpected hazards.",
    seoTitle: "Safety & Emergency Preparedness Simulations | Adaptience",
    seoDescription:
      "Practise navigating emergencies, safety challenges and unexpected situations through interactive preparedness simulations before the stakes are real.",
  },
  {
    slug: "change-uncertainty",
    name: "Change & Uncertainty",
    description: "Changing circumstances, transitions, unfamiliar situations and disrupted expectations.",
    seoTitle: "Change, Uncertainty & Adaptability Simulations | Adaptience",
    seoDescription:
      "Practise navigating unexpected change, uncertainty and unfamiliar situations through interactive simulations designed to develop adaptability.",
  },
  {
    slug: "setbacks-adversity",
    name: "Setbacks & Adversity",
    description: "Failure, rejection, disappointment, mistakes, loss and situations requiring recovery and moving forward.",
    seoTitle: "Setbacks & Adversity Simulations | Adaptience",
    seoDescription:
      "Practise navigating failure, rejection, disappointment and other setbacks through interactive simulations of difficult real-life situations.",
  },
];

// status: "available" | "coming-soon"
export const SIMULATIONS = [
  {
    id: "they-dont-want-me-here",
    slug: "they-dont-want-me-here",
    simulationId: "sim_000001",
    sceneCount: 6,
    title: "They Don't Want Me Here",
    description:
      "You realise the people you sit with may not want you around anymore. Practise how you respond when you feel left out.",
    category: "social-relationships",
    audience: "Children",
    ageRange: "Ages 10–14",
    capabilities: ["Staying calm under pressure", "Putting difficult feelings into words", "Reaching out for support", "Deciding how to respond to exclusion"],
    status: "available",
    duration: "8–10 min",
    image: "/og/they-dont-want-me-here-1200x630.jpg",
    route: "/simulations/they-dont-want-me-here",
    previewRoute: "/simulations/they-dont-want-me-here/preview",
    seoTitle: "Handling Social Exclusion: Interactive Life Simulation | Adaptience",
    seoDescription:
      "Practise navigating social exclusion in an interactive Adaptience simulation designed to help you explore how you might respond before facing it for real.",
    ogTitle: "They Don't Want Me Here | Adaptience",
    ogDescription:
      "An interactive life simulation for practising how to navigate social exclusion before facing it for real.",
    ogImage:
      "https://adaptience.com/og/they-dont-want-me-here-1200x630.jpg",
    previewImage:
      "https://static.prod-images.emergentagent.com/jobs/62c4d2db-e67b-4f45-8693-2623831d62c9/images/07a90b21d530bca5949ec604d099ff28736c793b29e4d564fd829a1ad358a655.jpeg",
    previewTitle: "They Don't Want Me Here",
    previewSituation:
      "The people you usually sit with make it clear they don't want you around. Over lunch, a few messages, and the next morning at school, you decide — moment by moment — how to respond to being left out.",
    previewExpect:
      "You'll move through realistic moments and choose how to respond in your own words or voice. Afterwards you'll reflect on how you handled it, and a private Parent Practice Profile is created from how you engaged — never from what you typed or said.",
  },

  // ---- Coming soon placeholders (NOT playable) ----
  {
    // Simulation #2 — public card + preview/detail content. H5-F activation rehearsal state (Preview):
    // status "available" + public route; SEO/OG fields frozen below.
    id: "someone-i-met-online",
    slug: "someone-i-met-online",
    simulationId: "sim_000002",
    title: "Someone I Met Online",
    description: "Practise noticing when an online interaction begins to change — and deciding what to do next.",
    category: "safety-emergencies",
    audience: "Children",
    ageRange: "Ages 10–14",
    capabilities: [
      "Judging trust",
      "Protecting personal boundaries",
      "Making privacy decisions",
      "Recognising pressure",
      "Taking protective action",
      "Seeking help when needed",
    ],
    status: "available",
    duration: "Approx. 15–20 min",
    image: "/wildrush/title-wide.webp",
    route: "/simulations/someone-i-met-online",
    previewRoute: "/simulations/someone-i-met-online/preview",
    seoTitle: "Someone I Met Online | Online Safety Simulation for Children | Adaptience",
    seoDescription:
      "An interactive online-safety simulation for ages 10–14 that helps children practise noticing when an online interaction begins to change and deciding what to do next.",
    ogTitle: "Someone I Met Online | Adaptience",
    ogDescription:
      "An interactive online-safety simulation for ages 10–14 — practise noticing when an online interaction begins to change and deciding what to do next.",
    ogImage: "https://adaptience.com/og/someone-i-met-online-1200x630.jpg",
    previewImage: "/wildrush/title-wide.webp",
    previewTitle: "Someone I Met Online",
    previewLead: "An interactive online-safety simulation for ages 10–14.",
    previewSituation:
      "Practise noticing when an online interaction begins to change — and deciding what to do next. Your child will step into a fictional online game, make their own decisions and see how the interaction unfolds.",
    previewExpect:
      "Your child plays through the game and chooses how to respond, moment by moment. Afterwards they'll reflect on how it unfolded, and a private Practice Report is created for parents and guardians.",
  },
  {
    id: "home-alone-after-school",
    slug: "home-alone-after-school",
    title: "Home Alone After School",
    description: "You're home by yourself and something doesn't go to plan. Practise staying capable and calm.",
    category: "everyday-life-skills",
    audience: "Children",
    ageRange: "Ages 10–14",
    capabilities: [],
    status: "coming-soon",
    duration: "8–10 min",
    image: null,
    route: null,
  },
  {
    id: "i-lost-something-important",
    slug: "i-lost-something-important",
    title: "I Lost Something Important",
    description: "Your phone — or a key — is suddenly gone. Practise thinking clearly instead of panicking.",
    category: "everyday-life-skills",
    audience: "Children",
    ageRange: "Ages 10–14",
    capabilities: [],
    status: "coming-soon",
    duration: "6–8 min",
    image: null,
    route: null,
  },
  {
    id: "the-group-chat-turned-on-me",
    slug: "the-group-chat-turned-on-me",
    title: "The Group Chat Turned On Me",
    description: "Messages about you start appearing in a group you're part of. Practise how you handle it.",
    category: "social-relationships",
    audience: "Children",
    ageRange: "Ages 10–14",
    capabilities: [],
    status: "coming-soon",
    duration: "8–10 min",
    image: null,
    route: null,
  },
  {
    id: "everyones-doing-it",
    slug: "everyones-doing-it",
    title: "Everyone's Doing It",
    description: "A group wants you to join in on something you're unsure about. Practise deciding for yourself.",
    category: "decisions-pressure",
    audience: "Children",
    ageRange: "Ages 10–14",
    capabilities: [],
    status: "coming-soon",
    duration: "8–10 min",
    image: null,
    route: null,
  },
  {
    id: "the-dare",
    slug: "the-dare",
    title: "The Dare",
    description: "You're dared to do something risky while everyone watches. Practise holding your ground.",
    category: "decisions-pressure",
    audience: "Children",
    ageRange: "Ages 10–14",
    capabilities: [],
    status: "coming-soon",
    duration: "6–8 min",
    image: null,
    route: null,
  },
  {
    id: "a-stranger-wants-to-help",
    slug: "a-stranger-wants-to-help",
    title: "A Stranger Wants to Help",
    description: "Someone you don't know offers to help you when you're on your own. Practise staying safe.",
    category: "safety-emergencies",
    audience: "Children",
    ageRange: "Ages 10–14",
    capabilities: [],
    status: "coming-soon",
    duration: "6–8 min",
    image: null,
    route: null,
  },
  {
    id: "when-something-goes-wrong-at-home",
    slug: "when-something-goes-wrong-at-home",
    title: "When Something Goes Wrong at Home",
    description: "A small emergency happens while the adults aren't around. Practise responding calmly.",
    category: "safety-emergencies",
    audience: "Children",
    ageRange: "Ages 10–14",
    capabilities: [],
    status: "coming-soon",
    duration: "8–10 min",
    image: null,
    route: null,
  },
  {
    id: "starting-at-a-new-school",
    slug: "starting-at-a-new-school",
    title: "Starting at a New School",
    description: "Everything and everyone is unfamiliar on your first day. Practise finding your footing.",
    category: "change-uncertainty",
    audience: "Children",
    ageRange: "Ages 10–14",
    capabilities: [],
    status: "coming-soon",
    duration: "8–10 min",
    image: null,
    route: null,
  },
  {
    id: "i-didnt-make-the-team",
    slug: "i-didnt-make-the-team",
    title: "I Didn't Make the Team",
    description: "You worked hard and still didn't get picked. Practise moving forward after disappointment.",
    category: "setbacks-adversity",
    audience: "Children",
    ageRange: "Ages 10–14",
    capabilities: [],
    status: "coming-soon",
    duration: "6–8 min",
    image: null,
    route: null,
  },
  {
    id: "i-failed-the-test",
    slug: "i-failed-the-test",
    title: "I Failed the Test",
    description: "A result comes back far worse than you hoped. Practise how you recover and try again.",
    category: "setbacks-adversity",
    audience: "Children",
    ageRange: "Ages 10–14",
    capabilities: [],
    status: "coming-soon",
    duration: "6–8 min",
    image: null,
    route: null,
  },
];

export const categoryName = (slug) => (CATEGORIES.find((c) => c.slug === slug) || {}).name || "";
export const categoryMeta = (slug) => CATEGORIES.find((c) => c.slug === slug) || null;
export const findSimulation = (slug) => SIMULATIONS.find((s) => s.slug === slug) || null;
